import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";

export default function KamToolboxesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [kind, setKind] = useState("template");
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sessionDate, setSessionDate] = useState("");
  const [sessionStatus, setSessionStatus] = useState("planned");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [linkedReportId, setLinkedReportId] = useState("");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [toolboxRes, usersRes] = await Promise.all([
        api.get("/kam/toolboxes", { params: { kind } }),
        api.get("/users"),
      ]);
      setItems(Array.isArray(toolboxRes?.data?.items) ? toolboxRes.data.items : []);
      setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Toolboxen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  async function createToolbox(event) {
    event.preventDefault();
    if (!title.trim()) {
      setError("Titel is verplicht.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setNotice("");
      const payload = {
        kind,
        title: title.trim(),
        subject: subject.trim(),
        content: content.trim(),
        status: kind === "template" ? "draft" : sessionStatus,
      };
      if (kind === "session") {
        payload.participants = selectedParticipantIds;
        payload.sessionDate = sessionDate || undefined;
        if (linkedReportId.trim()) payload.linkedReportIds = [linkedReportId.trim()];
      }

      await api.post("/kam/toolboxes", payload);
      setTitle("");
      setSubject("");
      setContent("");
      setSessionDate("");
      setSessionStatus("planned");
      setSelectedParticipantIds([]);
      setLinkedReportId("");
      setNotice("Toolbox opgeslagen.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Toolbox opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function confirmAttendance(toolboxId) {
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.post(`/kam/toolboxes/${toolboxId}/confirm`, {});
      setNotice("Aanwezigheid bevestigd.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Bevestigen mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function exportAttendance(toolboxId) {
    try {
      setError("");
      const res = await api.get(`/kam/toolboxes/${toolboxId}/participants/export`, {
        params: { format: "csv" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `toolbox-attendance-${toolboxId}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || "Exporteren mislukt");
    }
  }

  function toggleParticipant(userId) {
    setSelectedParticipantIds((prev) => {
      const exists = prev.includes(userId);
      if (exists) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">KAM toolboxen</h2>
        <p className="text-sm text-gray-500">Templates, geplande sessies en aanwezigen.</p>
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}
      {notice ? <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</section> : null}

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <form onSubmit={createToolbox} className="grid gap-2 md:grid-cols-2">
          <label className="text-xs font-medium text-gray-600">
            Soort
            <select
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            >
              <option value="template">Template</option>
              <option value="session">Sessie</option>
            </select>
          </label>

          <label className="text-xs font-medium text-gray-600">
            Titel
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            />
          </label>

          <label className="text-xs font-medium text-gray-600 md:col-span-2">
            Onderwerp
            <input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            />
          </label>

          <label className="text-xs font-medium text-gray-600 md:col-span-2">
            Inhoud / instructie
            <textarea
              rows={4}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
            />
          </label>

          {kind === "session" ? (
            <>
              <label className="text-xs font-medium text-gray-600">
                Sessiedatum
                <input
                  type="date"
                  value={sessionDate}
                  onChange={(event) => setSessionDate(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                />
              </label>

              <label className="text-xs font-medium text-gray-600">
                Status
                <select
                  value={sessionStatus}
                  onChange={(event) => setSessionStatus(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                >
                  <option value="draft">draft</option>
                  <option value="planned">planned</option>
                  <option value="completed">completed</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </label>

              <label className="text-xs font-medium text-gray-600 md:col-span-2">
                Gekoppelde melding (KamReportId, optioneel)
                <input
                  value={linkedReportId}
                  onChange={(event) => setLinkedReportId(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                />
              </label>

              <div className="md:col-span-2">
                <p className="text-xs font-medium text-gray-600">Deelnemers</p>
                <div className="mt-1 grid max-h-40 gap-1 overflow-auto rounded border border-gray-200 p-2 md:grid-cols-2">
                  {users.map((user) => {
                    const id = String(user?._id || "");
                    const checked = selectedParticipantIds.includes(id);
                    return (
                      <label key={id} className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleParticipant(id)}
                        />
                        <span>{user?.naam || user?.email || id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
            >
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Lijst ({kind})</h3>
        {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
        {!loading && items.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen items.</p> : null}
        {!loading && items.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {items.map((item) => {
              const confirmations = Array.isArray(item?.confirmations) ? item.confirmations.length : 0;
              const participants = Array.isArray(item?.participants) ? item.participants.length : 0;
              return (
                <li key={item._id} className="rounded border border-gray-200 p-3 text-sm">
                  <p className="font-medium text-gray-900">{item.title}</p>
                  <p className="text-xs text-gray-500">
                    {item.subject || "-"} • status: {item.status || "-"}
                  </p>
                  {kind === "session" ? (
                    <p className="text-xs text-gray-500">
                      deelnemers: {participants} • bevestigd: {confirmations}
                    </p>
                  ) : null}
                  {kind === "session" ? (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => confirmAttendance(item._id)}
                        disabled={saving}
                        className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        Aanwezig bevestigen
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAttendance(item._id)}
                        className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                      >
                        Exporteer deelnemers
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
